import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { MonthlyEstimation } from "../models/MonthlyEstimation";

const roundTo2 = (v: unknown): number | null => {
	if (v === null || v === undefined || v === "") return null;
	const n = Number(v);
	if (!Number.isFinite(n)) return null;
	return parseFloat(n.toFixed(2));
};

const mapRow = (raw: Record<string, unknown>): MonthlyEstimation => ({
	Month: String(raw.Month ?? ""),
	ForecastedProduction: roundTo2(raw.ForecastedProduction),
	ActualProduction: roundTo2(raw.ActualProduction),
	LastChange: raw.LastChange == null ? null : String(raw.LastChange),
	User: raw.User == null ? null : String(raw.User),
	Number: Number(raw.Number),
	Name: String(raw.Name ?? ""),
	Year: Number(raw.Year),
	Forecast: (raw.Forecast ? 1 : 0) as 0 | 1,
});

export interface GetMonthlyEstimationParams {
	year: number;
	signal?: AbortSignal;
}

export const getMonthlyEstimation = async ({
	year,
	signal,
}: GetMonthlyEstimationParams): Promise<MonthlyEstimation[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "year", value: year }];

	const spName = "[SAL].[GetMonthlyEstimation]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching monthly estimation");
	}

	const rows = get(resp, "data.tables[0].rows", []) as Record<
		string,
		unknown
	>[];
	return rows.map(mapRow);
};

export interface UpdateMonthlyDataParams {
	Month: string;
	ForecastedProduction: number | null;
	ActualProduction: number | null;
}

export const updateMonthlyData = async ({
	Month,
	ForecastedProduction,
	ActualProduction,
}: UpdateMonthlyDataParams): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "GetAxiomData", value: false },
		{ name: "Month", value: Month },
		{ name: "ForecastedProduction", value: ForecastedProduction },
		{ name: "ActualProduction", value: ActualProduction },
	];

	const resp = await apiService.callV2("[SAL].[UpdateMonthlyData]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error updating monthly data");
	}
};

export interface GetAxiomDataParams {
	Month: string;
}

export const getAxiomData = async ({
	Month,
}: GetAxiomDataParams): Promise<void> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "GetAxiomData", value: true },
		{ name: "Month", value: Month },
		{ name: "ForecastedProduction", value: null },
		{ name: "ActualProduction", value: null },
	];

	const resp = await apiService.callV2("[SAL].[UpdateMonthlyData]", parameters);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching Axiom data");
	}
};
